import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import PrayIcon from '@mui/icons-material/AutoAwesome';
import CollectionsIcon from '@mui/icons-material/Collections';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import GroupIcon from '@mui/icons-material/Group';

/**
 * Empty State Components
 * Elegant empty state displays with warm, spiritual styling
 */

// Generic Empty State Component
export const EmptyState = ({
  icon: Icon = SearchIcon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  image,
  size = 'medium', // 'small', 'medium', 'large'
  sx = {},
}) => {
  const sizes = {
    small: { iconSize: 56, py: 4 },
    medium: { iconSize: 80, py: 6 },
    large: { iconSize: 100, py: 8 },
  };
  const sizeConfig = sizes[size];

  return (
    <Box
      sx={{
        textAlign: 'center',
        py: sizeConfig.py,
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...sx,
      }}
    >
      {image ? (
        <img
          src={image}
          alt=""
          style={{
            width: sizeConfig.iconSize * 2,
            height: sizeConfig.iconSize * 2,
            marginBottom: 24,
            opacity: 0.7,
          }}
        />
      ) : (
        <Box
          sx={{
            width: sizeConfig.iconSize + 40,
            height: sizeConfig.iconSize + 40,
            borderRadius: '50%',
            backgroundColor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <Icon
            sx={{
              fontSize: sizeConfig.iconSize,
              color: 'secondary.main',
              opacity: 0.6,
            }}
          />
        </Box>
      )}

      <Typography
        variant={size === 'small' ? 'h6' : 'h5'}
        sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          color: 'text.primary',
          fontWeight: 500,
          mb: 1,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 3,
          maxWidth: 380,
          lineHeight: 1.7,
        }}
      >
        {description}
      </Typography>

      {(actionLabel || secondaryActionLabel) && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          {actionLabel && onAction && (
            <Button
              variant="contained"
              size={size === 'small' ? 'medium' : 'large'}
              onClick={onAction}
            >
              {actionLabel}
            </Button>
          )}
          {secondaryActionLabel && onSecondaryAction && (
            <Button
              variant="outlined"
              size={size === 'small' ? 'medium' : 'large'}
              onClick={onSecondaryAction}
            >
              {secondaryActionLabel}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

// No Prayers Created Empty State
export const NoPrayersCreated = ({ onCreateClick }) => (
  <EmptyState
    icon={PrayIcon}
    title="No prayers yet"
    description="Share your first prayer with the community and connect with people around the world who share your faith."
    actionLabel="Create Your First Prayer"
    onAction={onCreateClick}
  />
);

// No Collected Prayers Empty State
export const NoCollectedPrayers = ({ onExploreClick }) => (
  <EmptyState
    icon={CollectionsIcon}
    title="You haven't collected any prayers"
    description="Explore the map and collect prayers from people around the world. Each collection shows support and solidarity."
    actionLabel="Explore Prayers"
    onAction={onExploreClick}
  />
);

// No Search Results Empty State
export const NoSearchResults = ({ searchTerm, onClearSearch }) => (
  <EmptyState
    icon={SearchIcon}
    title="No results found"
    description={
      searchTerm
        ? `We couldn't find any prayers matching "${searchTerm}". Try different keywords or clear your search.`
        : "Try adjusting your search or filters to find what you're looking for."
    }
    actionLabel={searchTerm ? "Clear Search" : undefined}
    onAction={onClearSearch}
  />
);

// No Notifications Empty State
export const NoNotifications = () => (
  <EmptyState
    icon={NotificationsNoneIcon}
    title="No notifications"
    description="You're all caught up! We'll notify you when there's something new."
  />
);

// No Messages Empty State
export const NoMessages = () => (
  <EmptyState
    icon={MailOutlineIcon}
    title="No messages"
    description="You don't have any messages yet. Start connecting with other members of the community."
  />
);

// No Circle Prayers Empty State
export const NoCirclePrayers = ({ onCreateClick, circleName }) => (
  <EmptyState
    icon={GroupIcon}
    title="No prayers in this circle"
    description={`Be the first to share a prayer with ${circleName || 'this circle'}. Your community is here to support you.`}
    actionLabel="Write a Prayer"
    onAction={onCreateClick}
  />
);

// No Circles Joined Empty State
export const NoCirclesJoined = ({ onDiscoverClick, onCreateClick }) => (
  <EmptyState
    icon={GroupIcon}
    title="No prayer circles yet"
    description="Join a circle to connect with others who share your faith and prayer intentions."
    actionLabel="Discover Circles"
    onAction={onDiscoverClick}
    secondaryActionLabel="Create Circle"
    onSecondaryAction={onCreateClick}
  />
);

// Error State (for failed data fetches)
export const ErrorState = ({ onRetry, errorMessage }) => (
  <Box
    sx={{
      textAlign: 'center',
      py: 8,
      px: 2,
    }}
  >
    <Typography
      variant="h5"
      gutterBottom
      sx={{ color: 'error.main', fontWeight: 500 }}
    >
      Oops! Something went wrong
    </Typography>

    <Typography
      variant="body1"
      color="text.secondary"
      sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
    >
      {errorMessage || "We couldn't load the data. Please try again."}
    </Typography>

    {onRetry && (
      <Button
        variant="contained"
        color="primary"
        onClick={onRetry}
        size="large"
      >
        Try Again
      </Button>
    )}
  </Box>
);

// No Data Generic (customizable)
export const NoData = ({
  title = "No data available",
  description = "There's nothing to display right now.",
  icon: Icon = SearchIcon,
}) => (
  <EmptyState
    icon={Icon}
    title={title}
    description={description}
  />
);

export default {
  EmptyState,
  NoPrayersCreated,
  NoCollectedPrayers,
  NoSearchResults,
  NoNotifications,
  NoMessages,
  NoCirclePrayers,
  NoCirclesJoined,
  ErrorState,
  NoData,
};
